/**
 * AnonInfoBanner — PRI-503 signed-out "what is this" banner on app.*.
 *
 * Post-replatform, anonymous traffic lands directly on the product surface at
 * app.primateintelligence.ai (marketing lives on the apex). First-time
 * visitors arriving via a deep link / 301 need one line of context and a way
 * back to the marketing site. Slim, dismissible (persisted), signed-out only.
 *
 * App-plane component (brand asset — logo rule: Darwin_v3_vinyl_transparent.png).
 */
import { useState } from 'react'
import { L } from '../../core/components/v2/pv2-shared'
import { useIsMobile, detectIPad } from '../../core/hooks/useIsMobile'

export const ANON_BANNER_DISMISSED_KEY = 'pv2-anon-info-banner-dismissed'

/** Marketing site (apex is canonical; www 308s to it). */
const MARKETING_URL = 'https://primateintelligence.ai'

export function AnonInfoBanner() {
  const isMobile = useIsMobile()
  // Desktop renders a fixed icon strip (zIndex 100) down the left edge; start
  // the banner to its right so the sidebar toggle stays clickable underneath.
  const leftOffset = isMobile ? 0 : detectIPad() ? 56 : 48
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ANON_BANNER_DISMISSED_KEY) === 'true'
    } catch {
      return false
    }
  })

  if (dismissed) return null

  const dismiss = () => {
    try {
      localStorage.setItem(ANON_BANNER_DISMISSED_KEY, 'true')
    } catch {
      /* storage unavailable — dismiss for this render only */
    }
    setDismissed(true)
  }

  return (
    <div
      data-testid="anon-info-banner"
      role="complementary"
      aria-label="About Primate Intelligence"
      style={{
        position: 'fixed', top: 0, left: leftOffset, right: 0, zIndex: 8500,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '7px 40px 7px 16px',
        background: L.textPrimary, color: '#fff',
        fontSize: 13, lineHeight: 1.4, textAlign: 'center',
      }}
    >
      <img
        src="/Darwin_v3_vinyl_transparent.png"
        alt=""
        style={{ height: 18, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
      />
      <span style={{ minWidth: 0 }}>
        You&rsquo;re trying <strong style={{ fontWeight: 600 }}>Darwin</strong> — real-time video
        understanding by Primate Intelligence.{' '}
        <a
          data-testid="anon-info-banner-link"
          href={MARKETING_URL}
          style={{ color: '#fff', fontWeight: 600, textDecoration: 'underline', whiteSpace: 'nowrap' }}
        >
          What is this? →
        </a>
      </span>
      <button
        data-testid="anon-info-banner-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
          fontSize: 16, lineHeight: 1, cursor: 'pointer', padding: '4px 8px',
        }}
      >
        ×
      </button>
    </div>
  )
}
